import {
  ChangeDetectionStrategy,
  Component,
  computed,
  effect,
  inject,
  input,
  linkedSignal,
  output,
  signal,
} from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';

import { Amount, PaxAmount } from '@fcom/dapi';
import { LanguageService } from '@fcom/ui-translate';
import { AirCalendarList } from '@fcom/dapi/api/models/air-calendar-list';
import { LocalDate } from '@fcom/core/utils/tz-date';
import { isPresent } from '@fcom/core/utils/utils';
import { GlobalBookingTripDates } from '@fcom/common';
import { TripType } from '@fcom/core/interfaces/triptype.interface';
import { ButtonTheme, ButtonSize, ButtonMode, ModalButtons, NotificationTheme } from '@fcom/ui-components';

import { BookingWidgetGtmService } from '../../services/booking-widget-gtm.service';
import { mapPaxAmountToPaxBreakdown } from '../../utils/utils.pax-amount';

@Component({
  selector: 'fin-air-calendar',
  templateUrl: 'air-calendar.component.html',
  styleUrls: ['./air-calendar.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class AirCalendarComponent {
  private languageService = inject(LanguageService);
  private bookingWidgetGtmService = inject(BookingWidgetGtmService);

  readonly ButtonTheme = ButtonTheme;
  readonly ButtonSize = ButtonSize;
  readonly ButtonMode = ButtonMode;
  readonly TripType = TripType;
  readonly ModalButtons = ModalButtons;

  readonly paxAmount = input<PaxAmount>();

  readonly travelDates = input<GlobalBookingTripDates>();

  readonly tripType = input<TripType>();

  readonly prices = input<AirCalendarList>();

  readonly isAward = input(false);

  readonly totalPoints = input<number>();

  readonly selectDates = output<{ departureDate: LocalDate; returnDate: LocalDate }>();

  readonly continue = output<void>();

  private readonly passengerTranslations = toSignal(
    this.languageService.translate<Record<string, string>>('passenger')
  );

  readonly startingPrice = signal<Amount>({ amount: '0', currencyCode: 'EUR' });
  readonly continueDisabled = signal(true);
  readonly airCalendarOpen = linkedSignal<AirCalendarList | undefined, boolean>({
    source: this.prices,
    computation: (prices, previous) => isPresent(previous) && isPresent(prices),
  });
  readonly NotificationTheme = NotificationTheme;

  protected readonly paxAmountBreakdown = computed(() => {
    const paxAmount = this.paxAmount();
    const translations = this.passengerTranslations();
    return paxAmount && translations ? mapPaxAmountToPaxBreakdown(paxAmount, translations) : '';
  });

  protected readonly preselectedDeparture = computed(() => this.travelDates()?.departureDate?.toString());

  protected readonly preselectedReturn = computed(() => this.travelDates()?.returnDate?.toString());

  constructor() {
    effect(() => {
      if (this.airCalendarOpen()) {
        this.bookingWidgetGtmService.pageViewEvent('/price-matrix');
      }
    });
  }

  selectCalendarDates({ dates, price }: { dates: string[]; price?: Amount }): void {
    const departureDate = new LocalDate(dates[0]);
    const returnDate = dates[1] ? new LocalDate(dates[1]) : undefined;

    this.startingPrice.set(price);
    this.selectDates.emit({ departureDate, returnDate });
    this.bookingWidgetGtmService.trackElementEvent(
      'price-matrix',
      `DEPARTURE: ${departureDate}, RETURN: ${returnDate}`
    );
    // TODO: This is a really simple solution for enabling/disabling search from the matrix, do we need a better one?s
    this.continueDisabled.set(false);
  }

  navigateToBookingFlow(): void {
    this.continue.emit();
  }

  closeAirCalendar(): void {
    this.startingPrice.set({ amount: '0', currencyCode: 'EUR' });
    this.continueDisabled.set(true);
  }
}
